import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { TextComponentStyles } from 'lib/interfaces/ui-components';
import Checkbox from 'verify/components/Checkbox';
import ThemedParagraph from 'verify/components/ThemedParagraph';

export type AgreeToPolicyType = 'barcode' | 'biometric' | 'standard';

const TermsNoticeStyled = styled(ThemedParagraph).attrs(() => ({ as: 'small' }))<{
  styles: TextComponentStyles;
}>`
  ${({ styles }) => {
    /* TODO [strict-migration] The following is not typed properly; please fix! */
    const untypedStyles = styles as any;
    return {
      display: 'block',
      marginTop: `${SPACINGS.base}px`,
      marginBottom: `${SPACINGS.base * 2}px`,
      color: colorCss(untypedStyles['text-color'].base.base) as any,
      fontSize: sizeCss(untypedStyles['font-size'].base.base) as any,
      lineHeight: sizeCss(untypedStyles['line-height'].base.base) as any,
      '& , a': {
        fontSize: sizeCss(untypedStyles['font-size'].base.base) as any,
        lineHeight: sizeCss(untypedStyles['line-height'].base.base) as any,
      },
    };
  }}
`;

const TermsNotice = styled(ThemedParagraph).attrs(() => ({ as: 'small' }))`
  display: block;
  margin-top: ${SPACINGS.base}px;
  margin-bottom: ${SPACINGS.base * 2}px;
  ${({ theme }) => ({
    color: theme.colorFontSmall,
    fontSize: theme.fontSizeSmall,
    lineHeight: theme.lineHeightSmall,
    '&, a': {
      fontSize: theme.fontSizeSmall,
      lineHeight: theme.lineHeightSmall,
    },
  })}
`;

interface DisclaimerTextProps {
  agreeToPolicyType?: AgreeToPolicyType;
  styles?: TextComponentStyles;
}

export const DisclaimerText = ({ agreeToPolicyType = 'standard', styles }: DisclaimerTextProps) => {
  const { t } = useTranslation();

  let disclaimerText;
  switch (agreeToPolicyType) {
    case 'biometric':
      disclaimerText = t('intro.biometric-agree-to-policy');
      break;
    case 'standard':
      disclaimerText = t('intro.agree-to-policy');
      break;
    case 'barcode':
      disclaimerText = t('intro.barcode-agree-to-policy');
      break;
    default:
      disclaimerText = t('intro.agree-to-policy');
      break;
  }

  return (
    <>
      {styles != null ? (
        <TermsNoticeStyled styles={styles}>
          <Markdown>{disclaimerText}</Markdown>
        </TermsNoticeStyled>
      ) : (
        <TermsNotice>
          <Markdown>{disclaimerText}</Markdown>
        </TermsNotice>
      )}
    </>
  );
};

const TermsNoticeCheckboxContainer = styled(ThemedParagraph).attrs(() => ({ as: 'div' }))`
  display: flex;

  border-top: 1px solid ${COLORS.gray20};
  /* This expects to be under a header block which has margin-bottom: 32px; */
  margin-top: ${SPACINGS.base * -1}px;
  padding-top: ${SPACINGS.base * 3}px;
  margin-bottom: ${SPACINGS.base * 5}px;

  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: ${({ theme }) => theme.lineHeightSmall};

  &,
  a {
    font-size: ${({ theme }) => theme.fontSizeSmall};
    line-height: ${({ theme }) => theme.lineHeightSmall};
  }
`;

const TermsNoticeCheckbox = styled(Checkbox)`
  align-items: flex-start;
`;

interface DisclaimerCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const DisclaimerCheckbox = ({ checked, onChange }: DisclaimerCheckboxProps) => {
  const { t } = useTranslation();

  return (
    <TermsNoticeCheckboxContainer>
      <TermsNoticeCheckbox
        id='agree-to-policy-checkbox'
        data-test='agree-to-policy-checkbox'
        value={checked}
        onChange={onChange}
        label={t('intro.agree-to-policy')}
      />
    </TermsNoticeCheckboxContainer>
  );
};
